/**
 * The multi-parcel consignment editor, shared by the two hosts that build one from a plan:
 * `Combining` (a group merged into one shipment) and `SingleItemOptions` (one item put into several
 * boxes). It is the parcel-count picker, the arranger and the dimensions section - nothing else.
 *
 * Deliberately NOT included: the radio set, the summary alert and the Confirm button. Those differ per
 * host, and so does the commit - Combining creates a new item through createGroup, the single-item host
 * patches the existing item with a SaveItemsAction. Keeping the commit out of here is what stops this
 * becoming one component with a mode switch.
 *
 * The plan is owned by the HOST's `useParcelPlan`, not by this component. That is what lets each host
 * seed it differently (a group sums every item's weight; one item starts from its own dimensions) and
 * read the finished plan straight off the hook at commit time, with no reporting effect in between.
 */
import { P2GBox, P2GText } from '@parcel2go/core-ui';
import { useState } from 'react';
import { Dimensions, PackageApiModel } from '~/lib/api/smartsend/types';
import SidePanelSection from '../../../../../components/sidepanel-section';
import { DimensionsSelection } from '../DimensionsSelection';
import { ContentFieldContext, ParcelArranger } from './ParcelArranger';
import { ParcelCountPicker } from '../panels/ParcelCountPicker';
import { UseParcelPlan } from '~/apps/smartsend/lib/parcels/useParcelPlan';
import { resolveRowDrag } from '~/apps/smartsend/lib/parcels/planEdits';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

export interface ShipmentDimensionsSectionProps {
  dimensions: Dimensions;
  /** Drives the heading only - one parcel reads as the shipment's dimensions, several as additional. */
  parcelCount: number;
  fallback: Dimensions;
  onUpdate: (dimensions: Dimensions) => void;
  getPackages?: () => Promise<PackageApiModel[]>;
}

/**
 * Shared so the read-only merge case in `Combining` and the multi-parcel case here cannot drift on the
 * heading or the props handed to `DimensionsSelection`.
 */
export const ShipmentDimensionsSection: React.FC<ShipmentDimensionsSectionProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const isSingleParcel = props.parcelCount === 1;

  return (
    <SidePanelSection
      title={
        isSingleParcel
          ? t('NEW_SHIPMENT_DIMENSION_SUBTITLE', { defaultValue: 'Updated Shipment Dimensions' })
          : t('AMEND_BOX_DIMENSION_SUBTITLE', { defaultValue: 'Amend Box Dimensions' })
      }
    >
      {!isSingleParcel && (
        <P2GBox mb={1}>
          <P2GText variant="body2">
            <strong>
              {t('TOTAL_SHIPMENT_DIMENSION_NOTE', {
                defaultValue:
                  'Note: Changes to this will feed into your consignment. Weight will be distributed and dimensions will be applied to each parcel.'
              })}
            </strong>
          </P2GText>
        </P2GBox>
      )}
      <DimensionsSelection
        startMinimised={false}
        dimensions={props.dimensions}
        fallback={props.fallback}
        onUpdate={props.onUpdate}
        getPackages={props.getPackages}
      />
    </SidePanelSection>
  );
};

export interface ConsignmentBuilderProps {
  parcels: UseParcelPlan;
  /** Highest count the allocator will actually honour - from consignmentParcelBound. */
  maxParcels: number;
  content: ContentFieldContext;
  getPackages: () => Promise<PackageApiModel[]>;
  dimensionsFallback: Dimensions;
  /**
   * Picking 1x means something different per host - Combining switches to its merge radio, the
   * single-item host clears the consignment - so the hosts decide, not this component.
   */
  onCollapseToSingle?: () => void;
}

export const ConsignmentBuilder: React.FC<ConsignmentBuilderProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { plan, dimensions, setParcelCount, setDimensions, setPlan, edit } = props.parcels;

  // The plan is authoritative for how many parcels there actually are - the allocator may have
  // clamped below what was asked for.
  const displayedParcelCount = plan.Parcels.length;

  /**
   * Which dimensions control is on screen.
   *
   * The two cannot both be: setDimensions pushes one size onto EVERY parcel (see applyDimensions), so
   * a shipment-wide edit silently overwrites anything set per box. Previewing gets the shipment-wide
   * section - one size for all boxes, with the presets - and opening the editor swaps it for the
   * per-box controls inside each box card. Same shape the item panel and Splitting already use.
   */
  const [previewing, setPreviewing] = useState(true);

  /**
   * One route to "everything in one box", shared by picking 1x and by dragging the last row out of the
   * second-to-last parcel. Reseeding rather than keeping the dragged plan is deliberate: it is the
   * canonical single-parcel plan, and the two ways of getting here must not produce different ones.
   */
  const collapseToSingle = () => {
    props.onCollapseToSingle?.();
    setParcelCount(1);
  };

  /**
   * A row dragged from one preview parcel card onto another. Refusals are already filtered out by the
   * card, but resolveRowDrag is the authority - it is what distinguishes a plain move from a collapse.
   */
  const onDragRowToParcel = (contentUID: string, targetParcelUID: string) => {
    const outcome = resolveRowDrag(plan, contentUID, targetParcelUID);

    if (outcome.kind === 'SEND_AS_SINGLE') {
      collapseToSingle();
    } else if (outcome.kind === 'PLAN') {
      setPlan(outcome.plan);
    }
  };

  return (
    <>
      <SidePanelSection
        title={t('SHIPMENT_BOXES_BEING_SHIPPED', { defaultValue: 'How many boxes are you going to use?' })}
      >
        <ParcelCountPicker
          availableParcels={props.maxParcels}
          selected={displayedParcelCount}
          justify="flex-start"
          helperText={null}
          onSelect={(s) => (s === 1 ? collapseToSingle() : setParcelCount(s))}
        />
      </SidePanelSection>
      <SidePanelSection title={t('SHIPMENT_SUMMARY_SUBTITLE', { defaultValue: 'Shipment Summary' })}>
        <P2GBox py={2}>
          {/* No remount key: the plan is controlled, so changing the parcel count reseeds it
              directly rather than needing the child's state thrown away. */}
          <ParcelArranger
            plan={plan}
            outcome="ONE_SHIPMENT"
            edit={edit}
            arrange={edit}
            canSplitRows
            content={props.content}
            loadPreview={true}
            preview={previewing}
            onPreviewChange={setPreviewing}
            // No effect while previewing - GroupingPanelParcels is what reads it - so it can be passed
            // unconditionally and the swap stays expressed in one place below.
            perParcelDimensions={{ getPackages: props.getPackages }}
            onDragRowToParcel={onDragRowToParcel}
          />
        </P2GBox>
      </SidePanelSection>
      {previewing && (
        <ShipmentDimensionsSection
          dimensions={dimensions}
          parcelCount={displayedParcelCount}
          fallback={props.dimensionsFallback}
          onUpdate={setDimensions}
          getPackages={props.getPackages}
        />
      )}
    </>
  );
};
